import { useMemo } from "react";

import { MapState } from "@/sx-layout/hooks/useMapState";

type BoundingBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type ElementSize = {
  width: number;
  height: number;
};

export const useFloatingRightPosition = (
  boundingBox: BoundingBox,
  mapState: MapState,
  elementSize: ElementSize,
  margin: number
) => {
  const { mapRatio, viewport } = mapState;

  return useMemo(() => {
    const viewportLeft = (viewport?.left ?? 0) * mapRatio;
    const viewportRight = ((viewport?.left ?? 0) + (viewport?.width ?? 0)) * mapRatio;
    const viewportTop = (viewport?.top ?? 0) * mapRatio;
    const viewportBottom = ((viewport?.top ?? 0) + (viewport?.height ?? 0)) * mapRatio;

    // 1. 水平位置 (left) の計算
    const calculateLeft = () => {
      const defaultRight = (boundingBox.x + boundingBox.width) * mapRatio + margin;
      const leftOpposite = boundingBox.x * mapRatio - margin - elementSize.width;

      const spaceRight = viewportRight - defaultRight;
      const spaceLeft = boundingBox.x * mapRatio - viewportLeft;

      // 右に表示するスペースがない場合は左に表示
      if (spaceRight < elementSize.width && spaceLeft > elementSize.width + margin) {
        return { left: leftOpposite, originX: "right" as const };
      }

      // 左右に表示するスペースがない場合は表示領域の右の方に表示する
      if (spaceRight < elementSize.width && spaceLeft < elementSize.width + margin) {
        return { left: viewportRight - elementSize.width, originX: "right" as const };
      }

      // オブジェクトの右側に表示（デフォルト）
      return { left: defaultRight, originX: "left" as const };
    };

    // 2. 垂直位置 (top) の計算
    const calculateTop = () => {
      const preferredTop = (boundingBox.y + boundingBox.height / 2) * mapRatio - elementSize.height / 2;

      // オブジェクトの中央が表示領域外（上）の場合は表示領域の上に表示
      if (preferredTop < viewportTop) {
        return { top: viewportTop, originY: "top" as const };
      }
      // オブジェクトの中央が表示領域外（下）の場合は表示領域の下に表示
      if (viewportBottom < preferredTop + elementSize.height) {
        return { top: viewportBottom - elementSize.height, originY: "bottom" as const };
      }
      // オブジェクトの中央に表示（デフォルト）
      return { top: preferredTop, originY: "center" as const };
    };

    const { left, originX } = calculateLeft();
    const { top, originY } = calculateTop();

    return {
      top,
      left,
      transformOrigin: `${originY} ${originX}`,
    };
  }, [boundingBox, viewport, elementSize, margin, mapRatio]);
};
